import React from 'react';
import { Link } from 'react-router';
import map from 'lodash/map';
import classnames from 'classnames';
import LazyLoad from 'react-lazyload';

import { Button, Image } from 'shared/lib/SureDesignSystem';
import Footer from '../Footer/Footer';
import Review from '../Review/Review';

import teslaHeroSrc from './assets/hero/hero-tesla.png';
import teslaHeroSrcWebp from './assets/hero/hero-tesla.webp';

import icebergSrc from './assets/focus/iceberg.png';
import icebergSrcWebp from './assets/focus/iceberg.webp';
import birdsSrc from './assets/focus/birds.png';
import textTopSrc from './assets/focus/text-top.png';
import textTopMobileSrc from './assets/focus/text-top-mobile.png';
import textBottomSrc from './assets/focus/text-bottom.png';
import textBottomMobileSrc from './assets/focus/text-bottom-mobile.png';
import { isWebPSupported } from 'shared/lib/utils';

import styles from './LandingPlatform.module.css';

const solutions = [
  {
    title: 'Digital Distribution Core Features',
    description:
      'Rate, Quote, Bind, Issue. All the basics, but with world-class functionality. SURE PLATFORM utilizes AI technology taking place of the detailed work formerly done by insurance brokers, all on the fly.',
    pattern: require('./assets/solutions/distribute.png'),
    patternWebp: require('./assets/solutions/distribute.webp'),
  },
  {
    title: 'Policy Management on a Global Level',
    description:
      'SURE PLATFORM is the only technology available in all 50 states, providing ongoing service customers now demand. We support multi-currency and multi-language for brands worldwide.',
    pattern: require('./assets/solutions/policies.png'),
    patternWebp: require('./assets/solutions/policies.webp'),
  },
  {
    title: 'Digital Claims Intake System',
    description:
      'SURE makes it easier than ever to start and manage a claim and reduces the amount of human interaction needed to process a claim.',
    pattern: require('./assets/solutions/claims.png'),
    patternWebp: require('./assets/solutions/claims.webp'),
  },
  {
    title: 'Broker Portal For Selling Policies',
    description:
      'Our agent-facing tool takes care of business enabling insurance brokers to easily and quickly sell policies to customers, including easy upsell capabilities.',
    pattern: require('./assets/solutions/portal.png'),
    patternWebp: require('./assets/solutions/portal.webp'),
  },
];

function LandingPlatform() {
  return (
    <div className={styles.wrapper}>
      <div
        className={classnames(
          styles.heroSection,
          isWebPSupported
            ? styles.heroSectionBackground
            : styles.heroSectionBackgroundWebp
        )}
      >
        <div className={styles.heroSection_container}>
          <div className={styles.heroSection_titleWrapper}>
            <h2 className={styles.eyebrow}>SURE for GLOBAL BRANDS</h2>
            <h1 className={styles.heroSection_title}>
              Welcome to Your Insurance Platform
            </h1>
            <p className={styles.heroSection_description}>
              You’ve decided to maximize your customer value with the addition
              of insurance. And we’ve modernized the insurance industry with the
              world's most powerful enterprise tools. Now join the world’s
              leading organizations in the easily adaptable platform designed
              just for you, as soon as you need it.
            </p>
            <Link to="/contact">
              <Button secondary className={styles.heroSection_button}>
                Contact Partnerships
              </Button>
            </Link>
          </div>

          <Image
            src={teslaHeroSrc}
            webp={teslaHeroSrcWebp}
            className={styles.heroSection_image}
          />
        </div>
      </div>

      <Review
        review={{
          content: 'Much more compelling than anything else out there.',
          author: 'Elon Musk',
          position: 'CEO & FOUNDER',
          logo: require('./assets/reviews/tesla.png'),
        }}
      />

      <div className={styles.focusSection}>
        <div className={styles.focusSection_top}>
          <div className={styles.focusSection_container}>
            <h2 className={styles.focusSection_topTitle}>
              You focus on your customers. We’ll focus on everything else.
            </h2>
            <p className={styles.focusSection_topDescription}>
              SURE provides the tools that power global insurance companies and
              consumer brands to distribute and service any insurance policy
              digitally.
            </p>
            <ul
              className={classnames([
                styles.focusSection_list,
                styles.focusSection_topList,
              ])}
            >
              <li
                className={classnames([
                  styles.focusSection_listItem,
                  styles.focusSection_topListItem,
                ])}
              >
                Customer Experience
              </li>
            </ul>
          </div>
        </div>

        <div className={styles.focusSection_bottom}>
          <div className={styles.focusSection_container}>
            <div className={styles.iceberg_group}>
              <LazyLoad height={50} once>
                <img className={styles.birds} src={birdsSrc} alt="birds" />
              </LazyLoad>
              <LazyLoad height={50} once>
                <img
                  className={classnames([
                    styles.iceberg_groupTextTop,
                    styles.noMobile,
                  ])}
                  src={textTopSrc}
                  alt="What you see"
                />
              </LazyLoad>
              <LazyLoad height={150} once>
                <img
                  className={classnames([
                    styles.iceberg_groupTextTop,
                    styles.isMobile,
                  ])}
                  src={textTopMobileSrc}
                  alt="What you see"
                />
              </LazyLoad>
              <LazyLoad height={100} once>
                <img
                  className={classnames([
                    styles.iceberg_groupTextBottom,
                    styles.noMobile,
                  ])}
                  src={textBottomSrc}
                  alt="What you don't see"
                />
              </LazyLoad>
              <LazyLoad height={100} once>
                <img
                  className={classnames([
                    styles.iceberg_groupTextBottom,
                    styles.isMobile,
                  ])}
                  src={textBottomMobileSrc}
                  alt="What you don't see"
                />
              </LazyLoad>
              <LazyLoad height={810} once>
                <Image
                  className={styles.iceberg}
                  src={icebergSrc}
                  webp={icebergSrcWebp}
                  alt="iceberg"
                />
              </LazyLoad>
            </div>
            <ul className={styles.focusSection_list}>
              <li className={styles.focusSection_listItem}>APIs & SDKs</li>
              <li className={styles.focusSection_listItem}>
                CRM & Agent Portal
              </li>
              <li className={styles.focusSection_listItem}>
                Rating & Quoting Engine
              </li>
              <li className={styles.focusSection_listItem}>
                Bind & Issue Policy
              </li>
              <li className={styles.focusSection_listItem}>
                Policy Management
              </li>
              <li className={styles.focusSection_listItem}>
                Billing & Payments
              </li>
              <li className={styles.focusSection_listItem}>
                Claims Management
              </li>
              <li className={styles.focusSection_listItem}>
                Compliance & Regulation
              </li>
              <li className={styles.focusSection_listItem}>
                Cloud Infrastructure
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className={styles.chooseSolutionSection}>
        <div className={styles.chooseSolutionSection_container}>
          <h1 className={styles.chooseSolutionSection_title}>
            Choose the right solution for your business
          </h1>
          <p className={styles.chooseSolutionSection_description}>
            SURE’s professional services will customize and implement the
            platform for your business case.
          </p>

          <div className={styles.chooseSolutionSection_items}>
            {map(solutions, (solution, i) => (
              <div key={i} className={styles.chooseSolutionSection_item}>
                <div className={styles.chooseSolutionSection_itemTop}>
                  <LazyLoad height={126} once>
                    <Image
                      src={solution.pattern}
                      webp={solution.patternWebp}
                      className={styles.chooseSolutionSection_itemImage}
                    />
                  </LazyLoad>
                </div>

                <div className={styles.chooseSolutionSection_itemBottom}>
                  <h3 className={styles.chooseSolutionSection_itemTitle}>
                    {solution.title}
                  </h3>

                  <p className={styles.chooseSolutionSection_itemDescription}>
                    {solution.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={styles.manageSection}>
        <div className={styles.manageSection_container}>
          <div />
          <div>
            <div className={styles.manageSection_block}>
              <h2 className={styles.manageSection_title}>
                Seamless Customer Experience.
              </h2>
              <p className={styles.manageSection_description}>
                With SURE you maintain your brand look and feel across all your
                digital products
              </p>
            </div>
            <div className={styles.manageSection_block}>
              <h2 className={styles.manageSection_title}>
                Carrier Flexibility.
              </h2>
              <p className={styles.manageSection_description}>
                Easily add or remove carriers when needed, never beholden to
                just one.
              </p>
            </div>
            <div className={styles.manageSection_block}>
              <h2 className={styles.manageSection_title}>Launch Now.</h2>
              <p className={styles.manageSection_description}>
                SURE is committed to expediting your digital insurance program
                and getting you in market fast.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.leadGenSection}>
        <div className={styles.leadGenSection_container}>
          <h2 className={styles.leadGenSection_title}>
            Join the largest network of carriers for SURE.
          </h2>
          <p className={styles.leadGenSection_description}>
            Enhance the technology you use with brokers and customers. Bring
            your business into the 21st Century.
          </p>
          <Button className={styles.leadGenSection_button}>Learn more</Button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default LandingPlatform;
